/* Theme-owned gift cleanup. It must work even when the gift app embed is disabled. */
(function (scope) {
  'use strict';
  const core = scope.KateCartGiftCore;
  if (!core || scope.KateGiftSafety) return;
  const document = scope.document;
  const route = scope.Shopify?.routes?.root || '/';
  let queue = Promise.resolve(), suppressed = false, timer, updates = 0, pendingCheckout = false, replaying = false;
  const exclusive = task => (queue = queue.then(task, task));
  const failure = () => Object.assign(new Error('Unable to remove an unavailable free gift. Please refresh your cart and try again before checking out.'), { safetyFailure: true });
  const ordinary = cart => JSON.stringify(cart.items.filter(line => !core.managed(line))
    .map(line => [core.identity(line), line.quantity]).sort(([a], [b]) => a.localeCompare(b)));
  const cartData = value => {
    if (!Array.isArray(value?.items)) throw failure();
    return value;
  };
  async function request(path, body, signal, html = false) {
    const controller = signal ? null : new scope.AbortController();
    const timeout = controller && scope.setTimeout(() => controller.abort(), 12000);
    try {
      const response = await scope.fetch(route + path, { credentials: 'same-origin', cache: 'no-store', signal: signal || controller.signal,
        headers: { Accept: html ? 'text/html' : 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { method: 'POST', body: JSON.stringify(body) } : {}) });
      if (!response.ok) throw failure();
      return html ? response.text() : response.json();
    } finally { if (timeout) scope.clearTimeout(timeout); }
  }
  const fetchCart = async () => cartData(await request('cart.js'));
  function message(text) {
    let node = document.getElementById('kate-gift-safety-status');
    if (!node) {
      node = document.createElement('p'); node.id = 'kate-gift-safety-status';
      node.setAttribute('role', 'status'); node.setAttribute('aria-live', 'polite');
      (document.querySelector('.cart__recap, #mini-cart .drawer__footer, #main') || document.body).prepend(node);
    }
    node.textContent = text; node.hidden = !text;
  }
  function notify(cart, removedKeys, checkout) {
    for (const name of ['cart:updated', 'cart:refresh']) {
      document.dispatchEvent(new scope.CustomEvent(name, { detail: { cart, source: 'kate-gift-safety' } }));
    }
    message('An unavailable free gift was removed. You can continue checking out.');
    // Main-cart rows and totals are server rendered; a background cleanup needs a fresh page.
    if (!checkout && document.getElementById('checkout-selected')) scope.location.reload();
  }
  async function removeAllLocked(reason, { checkout = false } = {}) {
    suppressed = true;
    const before = await fetchCart();
    const gifts = before.items.filter(core.managed);
    if (!gifts.length) return { cart: before, removedKeys: [], bypassGiftFlow: true, reason };
    const removedKeys = gifts.map(line => line.key);
    // Never retry a lost write response: re-read and verify what actually happened.
    try { await request('cart/update.js', { updates: Object.fromEntries(removedKeys.map(key => [key, 0])) }); } catch {}
    let verified;
    try { verified = await fetchCart(); } catch { throw failure(); }
    if (verified.items.some(core.managed) || ordinary(before) !== ordinary(verified)) throw failure();
    notify(verified, removedKeys, checkout);
    return { cart: verified, removedKeys, bypassGiftFlow: true, reason };
  }
  const read = (projection, id) => {
    const node = projection.getElementById(id);
    if (!node) throw new Error('Missing gift configuration');
    const value = JSON.parse(node.textContent);
    return typeof value === 'string' ? JSON.parse(value) : value;
  };
  async function inspect(cart, keys, signal) {
    const root = document.getElementById('zyf-gift-promotions-root');
    if (!root || (!scope.KateCartGifts && root.dataset.kateGiftOwner !== 'drawer')) throw new Error('Gift app unavailable');
    const html = await request('cart?zyf_gift_runtime=1', undefined, signal, true);
    const projection = new scope.DOMParser().parseFromString(html, 'text/html');
    if (!projection.getElementById('zyf-gift-promotions-root')) throw new Error('Gift app disabled');
    const config = read(projection, 'zyf-gift-promotions-config');
    const facts = read(projection, 'zyf-gift-promotions-cart-context').items;
    if (!Array.isArray(config?.rules) || !config.rules.length || !Array.isArray(facts)) throw new Error('Gift activity unavailable');
    const now = Date.now();
    if (config.rules.some(rule => (rule.status && rule.status !== 'ENABLED') ||
      (rule.startsAtUtc && Date.parse(rule.startsAtUtc) > now) || (rule.endsAtUtc && Date.parse(rule.endsAtUtc) <= now))) throw new Error('Gift activity inactive');
    if (cart.items.some(line => !core.managed(line) && !core.external(line) && !facts.some(fact => String(fact.variantId) === String(line.variant_id)))) throw new Error('Gift facts unavailable');
    let allows = () => true;
    if (config.rules.some(rule => rule.allowedCountryCodes?.length)) {
      if (!scope.ZyfGiftCountry) throw new Error('Gift country unavailable');
      let detected = '';
      const gate = scope.ZyfGiftCountry(config, async attributes => { detected = attributes._zyf_gift_ip_country || ''; return cart; });
      await gate.sync({ ...cart, attributes: { ...cart.attributes, _zyf_gift_ip_country: '__read_only__' } });
      if (!detected) throw new Error('Gift country unavailable');
      allows = gate.allows;
    }
    const products = new Map(), stock = new Map(), choices = new Map();
    for (const rule of config.rules) for (const tier of rule.tiers) for (const candidate of tier.candidates) {
      if (!candidate.productHandle) throw new Error('Gift inventory unavailable');
      const candidates = products.get(candidate.productHandle) || [];
      candidates.push(candidate); products.set(candidate.productHandle, candidates);
    }
    await Promise.all([...products].map(async ([handle, candidates]) => {
      const product = await request(`products/${encodeURIComponent(handle)}?view=gift-stock`, undefined, signal);
      if (!Array.isArray(product.variants)) throw new Error('Gift inventory unavailable');
      for (const candidate of candidates) {
        const id = core.suffix(candidate.variantId);
        const variant = product.variants.find(item => String(item.id) === id);
        if (variant && typeof variant.giftAvailable !== 'boolean') throw new Error('Gift inventory unavailable');
        stock.set(id, variant?.giftAvailable === true);
      }
    }));
    // Keep an existing valid choice rather than replacing it with the first candidate.
    for (const line of cart.items.filter(core.managed)) {
      choices.set(`${line.properties[core.ATTR.rule]}:${line.properties[core.ATTR.tier]}`, String(line.variant_id));
    }
    const gifts = cart.items.filter(core.managed);
    if (gifts.length > 1 || gifts.some(line => core.giftState(config, cart, line, facts, stock, choices,
      Number(scope.Shopify?.currency?.rate || 1) || 1, keys, allows) !== 'valid')) throw new Error('Gift is no longer valid');
  }
  async function ensureLocked({ keys, checkout = false } = {}) {
    let cart = await fetchCart();
    if (suppressed) return removeAllLocked('gift-flow-bypassed', { checkout });
    if (!cart.items.some(core.managed) && !checkout) return { cart, removedKeys: [], bypassGiftFlow: false };
    const controller = new scope.AbortController();
    const timeout = scope.setTimeout(() => controller.abort(), 12000);
    let stop;
    try {
      const unavailable = new Promise((resolve, reject) => {
        stop = () => reject(new Error('Gift check timed out'));
        controller.signal.addEventListener('abort', stop, { once: true });
      });
      await Promise.race([inspect(cart, keys, controller.signal), unavailable]);
    } catch {
      controller.abort();
      return removeAllLocked('gift-unavailable-or-invalid', { checkout });
    } finally {
      scope.clearTimeout(timeout);
      if (stop) controller.signal.removeEventListener('abort', stop);
    }
    const latest = await fetchCart();
    if (ordinary(cart) !== ordinary(latest) || JSON.stringify(cart.items.filter(core.managed)) !== JSON.stringify(latest.items.filter(core.managed))) {
      throw Object.assign(new Error('Your cart changed. Please wait for it to finish updating and try again.'), { cartChanged: true });
    }
    return { cart: latest, removedKeys: [], bypassGiftFlow: false };
  }
  const ensure = options => exclusive(() => ensureLocked(options));
  scope.KateGiftSafety = { exclusive, ensure, ensureLocked, removeAllLocked, get suppressed() { return suppressed; } };
  function schedule(event) {
    if (event?.detail?.source === 'kate-gift-safety') return;
    scope.clearTimeout(timer);
    timer = scope.setTimeout(() => {
      if (updates || pendingCheckout || scope.KateCartIsUpdating?.()) return;
      void ensure().catch(error => message(error.message || failure().message));
    }, 150);
  }
  function checkoutTarget(target) {
    const button = target.closest?.('.cart__checkout-button, #checkout-selected, button[name="checkout"], input[type="submit"][name="checkout"]');
    if (button) return button;
    const link = target.closest?.('a[href]');
    if (link) {
      const url = new URL(link.href, scope.location.href);
      if (url.origin === scope.location.origin && /^\/([^/]+\/)?checkouts?(\/|$)/.test(url.pathname)) return link;
    }
    return null;
  }
  async function gate(target, form) {
    if (pendingCheckout) return;
    pendingCheckout = true;
    target?.setAttribute('aria-busy', 'true');
    try {
      if (updates || scope.KateCartIsUpdating?.()) throw new Error('Your cart is updating. Please wait and try again.');
      await scope.KateCustomSizeCartNormalizer?.normalize();
      const result = await ensure({ checkout: true });
      if (!result.cart.items.some(line => !core.managed(line))) { scope.location.assign(route + 'cart'); return; }
      replaying = true;
      try { if (form) form.requestSubmit(); else target.click(); } finally { replaying = false; }
    } catch (error) { message(error.message || failure().message); }
    finally { pendingCheckout = false; target?.removeAttribute('aria-busy'); }
  }
  // Window capture runs before the cart adapters and custom-size checkout handlers.
  scope.addEventListener('click', event => {
    if (replaying) return;
    const target = checkoutTarget(event.target);
    if (!target || target.disabled) return;
    event.preventDefault(); event.stopImmediatePropagation();
    void gate(target);
  }, true);
  scope.addEventListener('submit', event => {
    if (replaying || !event.target.matches?.('form.cart__recap, form[action$="/checkout"]')) return;
    event.preventDefault(); event.stopImmediatePropagation();
    void gate(event.submitter, event.target);
  }, true);
  for (const name of ['cart:updated', 'cart:refresh', 'variant:added', 'ajaxProduct:added', 'product:added']) document.addEventListener(name, schedule);
  document.addEventListener('line-item-quantity:change:start', () => { updates++; });
  document.addEventListener('line-item-quantity:change:end', () => { updates = Math.max(0, updates - 1); schedule(); });
  scope.addEventListener('pageshow', schedule);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) schedule(); });
  const drawer = document.getElementById('mini-cart');
  if (drawer) new scope.MutationObserver(schedule).observe(drawer, { childList: true, attributes: true, attributeFilter: ['open'] });
  schedule();
})(window);
